import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  TrendingUp, 
  Clock, 
  Download,
  RefreshCw,
  Calendar,
  Eye,
  ThumbsUp,
  Film,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import * as api from '@/lib/api';

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

// Custom Tooltip component for reports charts
const ReportsTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-card border border-border rounded-lg p-3 shadow-xl">
        <p className="text-sm font-medium mb-1">{label}</p>
        <p className="text-xs text-primary">Engagement: {payload[0].value}%</p>
      </div>
    );
  }
  return null;
};

export default function Reports() {
  const [latestReport, setLatestReport] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [latestRes, allRes] = await Promise.all([
        api.getLatestReport(),
        api.getReports(10)
      ]);
      setLatestReport(latestRes.data);
      setReports(allRes.data);
    } catch (error) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await api.generateReport();
      toast.success('Report generated successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Weekly Reports</h1>
          <p className="text-muted-foreground">Performance insights and growth metrics</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={fetchData} variant="outline" data-testid="refresh-reports-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={handleGenerate} disabled={generating} data-testid="generate-report-btn">
            {generating ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2" />
            )}
            Generate Report
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="space-y-6">
          <Skeleton className="h-40 w-full" />
          <div className="grid grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-80 w-full" />
        </div>
      ) : latestReport ? (
        <>
          {/* Report Header */}
          <Card className="bg-card border-border overflow-hidden">
            <div className="bg-gradient-to-r from-primary/20 to-secondary/20 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <Badge className="mb-2">Latest Report</Badge>
                  <h2 className="text-xl font-heading font-bold">
                    Weekly Performance Summary
                  </h2>
                  <p className="text-muted-foreground">
                    {formatDate(latestReport.week_start)} - {formatDate(latestReport.week_end)}
                  </p>
                </div>
                <div className="text-right">
                  <div className="flex items-center gap-2 text-green-500">
                    <TrendingUp className="w-5 h-5" />
                    <span className="text-2xl font-bold">+{latestReport.growth_rate}%</span>
                  </div>
                  <p className="text-sm text-muted-foreground">Growth rate</p>
                </div>
              </div>
            </div>
          </Card>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Film className="w-5 h-5 text-primary" />
                  <span className="text-sm text-muted-foreground">Clips Generated</span>
                </div>
                <p className="text-3xl font-heading font-bold">{latestReport.total_clips_generated}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <FileText className="w-5 h-5 text-secondary" />
                  <span className="text-sm text-muted-foreground">Clips Published</span>
                </div>
                <p className="text-3xl font-heading font-bold">{latestReport.total_clips_published}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <Eye className="w-5 h-5 text-accent" />
                  <span className="text-sm text-muted-foreground">Total Views</span>
                </div>
                <p className="text-3xl font-heading font-bold">{(latestReport.total_views || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card className="bg-card border-border">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <ThumbsUp className="w-5 h-5 text-green-500" />
                  <span className="text-sm text-muted-foreground">Total Likes</span>
                </div>
                <p className="text-3xl font-heading font-bold">{(latestReport.total_likes || 0).toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Charts and Insights */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Clips */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading">Top Performing Clips</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {latestReport.top_clips?.map((clip, index) => (
                    <div 
                      key={`clip-${clip.title?.slice(0, 15) || index}-${index}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/30"
                    >
                      <span className="text-lg font-bold text-primary">#{index + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{clip.title}</p>
                      </div>
                      <Badge variant="outline">
                        {(clip.views || 0).toLocaleString()} views
                      </Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Best Posting Times */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading">Best Posting Times</CardTitle>
              </CardHeader>
              <CardContent>
                {latestReport.best_posting_times?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={latestReport.best_posting_times}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis 
                        dataKey="day" 
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                      <Tooltip content={<ReportsTooltip />} />
                      <Bar 
                        dataKey="engagement" 
                        fill="hsl(var(--primary))"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[200px] flex items-center justify-center">
                    <p className="text-muted-foreground">No posting time data</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Trending Topics */}
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-heading">Trending Topics</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {latestReport.trending_topics?.map((topic, index) => (
                  <Badge 
                    key={`topic-${topic}-${index}`}
                    variant="outline"
                    className="px-4 py-2 text-sm"
                  >
                    {topic}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Previous Reports */}
          {reports.length > 1 && (
            <Card className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg font-heading">Previous Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reports.slice(1).map((report, index) => (
                    <div 
                      key={report.id || index}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <span className="text-sm">
                          {formatDate(report.week_start)} - {formatDate(report.week_end)}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>{report.total_clips_published} clips</span>
                        <span>{(report.total_views || 0).toLocaleString()} views</span>
                        <Badge variant="outline" className="text-green-500">
                          +{report.growth_rate}%
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Card className="bg-card border-border">
          <CardContent className="py-16 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No reports generated yet</p>
            <Button onClick={handleGenerate} disabled={generating}>
              {generating ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <FileText className="w-4 h-4 mr-2" />
              )}
              Generate First Report
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
